import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { Search, Package, AlertTriangle, Clock, Trash2, TrendingDown, Warehouse, Eye, Edit2, BarChart3, Download, ChevronRight, ChevronDown, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { formatDateOnly, formatDate } from '@/utils/dateFormat';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import api, { inventoryAPI, warehousesAPI } from '@/services/api';
import { EXPIRY_FILTERS, expiryParams, exportRowsToCsv } from '@/utils/stockExport';

const StockOverviewPage = () => {
  const [overview, setOverview] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stockLoading, setStockLoading] = useState(false);
  const [activeView, setActiveView] = useState('active');
  const [search, setSearch] = useState('');
  const [groupBy, setGroupBy] = useState('product');
  const [batchSearch, setBatchSearch] = useState('');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [includeZero, setIncludeZero] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [expandedBatches, setExpandedBatches] = useState([]);
  const [reconciling, setReconciling] = useState(false);
  
  // Dialog states
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [damageDialogOpen, setDamageDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [movements, setMovements] = useState([]);
  const [batches, setBatches] = useState([]);
  const [damageQty, setDamageQty] = useState('');
  const [damageReason, setDamageReason] = useState('');
  
  const { toast } = useToast();

  const fetchOverview = useCallback(async () => {
    try {
      const [overviewRes, warehousesRes] = await Promise.all([
        inventoryAPI.getStockOverview(),
        warehousesAPI.list({ is_active: true })
      ]);
      setOverview(overviewRes.data);
      setWarehouses(warehousesRes.data || []);
      
      // Auto-select first warehouse if available
      if (warehousesRes.data?.length > 0 && !selectedWarehouse) {
        setSelectedWarehouse(warehousesRes.data[0].id);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch stock overview', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast, selectedWarehouse]);

  const fetchWarehouseStock = useCallback(async () => {
    if (!selectedWarehouse) return;
    setStockLoading(true);
    try {
      const response = await inventoryAPI.getWarehouseStock(selectedWarehouse, {
        view: activeView,
        group_by: groupBy,
        search: search || undefined,
        batch_search: groupBy === 'batch' && batchSearch ? batchSearch : undefined,
        include_zero: groupBy === 'batch' ? includeZero : undefined,
        ...(groupBy === 'batch' ? expiryParams(expiryFilter) : {})
      });
      setStockData(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch stock data', variant: 'destructive' });
    } finally {
      setStockLoading(false);
    }
  }, [selectedWarehouse, activeView, search, groupBy, batchSearch, expiryFilter, includeZero, toast]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    if (selectedWarehouse) {
      fetchWarehouseStock();
    }
  }, [selectedWarehouse, activeView, fetchWarehouseStock]);

  const toggleProductBatches = async (item) => {
    if (expandedProduct === item.product_id) {
      setExpandedProduct(null);
      setExpandedBatches([]);
      return;
    }
    setExpandedProduct(item.product_id);
    setExpandedBatches([]);
    try {
      const res = await inventoryAPI.getProductBatches(item.product_id, selectedWarehouse);
      setExpandedBatches(res.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch batches', variant: 'destructive' });
    }
  };

  const handleReconcile = async () => {
    try {
      setReconciling(true);
      const res = await api.post('/inventory/reconcile-stock');
      toast({
        title: 'Stock figures reconciled',
        description: `${res.data.products_updated} of ${res.data.products_checked} products corrected from the batch ledger`
      });
      fetchOverview();
      if (selectedWarehouse) fetchWarehouseStock();
    } catch (error) {
      toast({
        title: 'Reconcile failed',
        description: error.response?.data?.detail || 'Could not reconcile stock figures',
        variant: 'destructive'
      });
    } finally {
      setReconciling(false);
    }
  };

  const handleExport = () => {
    const rows = stockData?.items || [];
    if (!rows.length) {
      toast({ title: 'Nothing to export', description: 'No stock rows in the current view' });
      return;
    }
    const columns = groupBy === 'batch'
      ? [
          { label: 'Product', key: 'product_name' },
          { label: 'SKU', key: 'product_sku' },
          { label: 'Batch', key: 'batch_number' },
          { label: 'Expiry', key: 'expiry_date' },
          { label: 'Qty', key: 'quantity' },
          { label: 'Cost', key: 'cost_price' },
          { label: 'Value', key: 'stock_value' }
        ]
      : [
          { label: 'Product', key: 'product_name' },
          { label: 'SKU', key: 'product_sku' },
          { label: 'Category', key: 'category_name' },
          { label: 'Total Qty', key: 'quantity' },
          { label: 'Batches', key: 'batch_count' },
          { label: 'Nearest Expiry', key: 'nearest_expiry' },
          { label: 'Value', key: 'stock_value' }
        ];
    exportRowsToCsv(rows, columns, `warehouse-stock-${groupBy}-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (selectedWarehouse) {
        fetchWarehouseStock();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, selectedWarehouse, fetchWarehouseStock]);

  const handleViewMovements = async (item) => {
    setSelectedItem(item);
    try {
      const response = await inventoryAPI.getStockMovements(item.product_id, {
        warehouse_id: selectedWarehouse
      });
      setMovements(response.data);
      setMovementDialogOpen(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch movements', variant: 'destructive' });
    }
  };

  const handleViewBatches = async (item) => {
    setSelectedItem(item);
    try {
      const response = await inventoryAPI.getProductBatches(item.product_id, selectedWarehouse);
      setBatches(response.data);
      setBatchDialogOpen(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch batches', variant: 'destructive' });
    }
  };

  const handleMarkDamaged = (item) => {
    setSelectedItem(item);
    setDamageQty('');
    setDamageReason('');
    setDamageDialogOpen(true);
  };

  const confirmMarkDamaged = async () => {
    if (!damageQty || parseFloat(damageQty) <= 0) {
      toast({ title: 'Error', description: 'Enter valid damaged quantity', variant: 'destructive' });
      return;
    }
    try {
      await inventoryAPI.markDamaged(selectedItem.stock_id, parseFloat(damageQty), damageReason);
      toast({ title: 'Success', description: 'Stock marked as damaged' });
      setDamageDialogOpen(false);
      fetchWarehouseStock();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to mark damaged', variant: 'destructive' });
    }
  };

  const getViewIcon = (view) => {
    switch (view) {
      case 'active': return Package;
      case 'low': return AlertTriangle;
      case 'dead': return Clock;
      case 'damaged': return Trash2;
      default: return Package;
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading...</div>;
  }

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <Toaster />
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Inventory Stock</h1>
          <p className="text-gray-500">Warehouse-wise stock management</p>
        </div>
        <Button
          variant="outline"
          onClick={handleReconcile}
          disabled={reconciling}
          title="Recompute product stock counters from the batch ledger"
          data-testid="reconcile-stock-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${reconciling ? 'animate-spin' : ''}`} />
          {reconciling ? 'Reconciling...' : 'Reconcile Stock Figures'}
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Stock Value</p>
                <p className="text-2xl font-bold text-teal-600">₹{overview?.summary?.total_stock_value?.toLocaleString() || 0}</p>
              </div>
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-teal-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Warehouses</p>
                <p className="text-2xl font-bold text-blue-600">{overview?.summary?.total_warehouses || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Warehouse className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Low Stock Items</p>
                <p className="text-2xl font-bold text-amber-600">{overview?.summary?.total_low_stock || 0}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Out of Stock</p>
                <p className="text-2xl font-bold text-red-600">{overview?.summary?.total_out_of_stock || 0}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Warehouse Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {overview?.warehouses?.map(wh => (
          <Card 
            key={wh.warehouse_id} 
            className={`bg-white border-2 shadow-sm cursor-pointer transition-all ${
              selectedWarehouse === wh.warehouse_id ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedWarehouse(wh.warehouse_id)}
            data-testid={`stock-warehouse-card-${wh.warehouse_id}`}
          >
            <CardContent className="pt-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Warehouse className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">{wh.warehouse_name}</p>
                  <p className="text-xs text-teal-600">{wh.warehouse_code}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-xs text-gray-500">Products</p>
                  <p className="font-bold text-gray-800">{wh.total_products}</p>
                </div>
                <div className="bg-amber-50 rounded p-2">
                  <p className="text-xs text-amber-600">Low Stock</p>
                  <p className="font-bold text-amber-600">{wh.low_stock_count}</p>
                </div>
                <div className="bg-red-50 rounded p-2">
                  <p className="text-xs text-red-600">Out</p>
                  <p className="font-bold text-red-600">{wh.out_of_stock_count}</p>
                </div>
              </div>
              <div className="mt-3 text-right">
                <p className="text-sm text-gray-500">Value: <span className="font-bold text-gray-800">₹{wh.stock_value?.toLocaleString()}</span></p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Stock Details */}
      {selectedWarehouse && (
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="pb-4 border-b border-gray-100">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <CardTitle className="text-lg text-gray-800">
                {stockData?.warehouse?.name} - Stock Details
              </CardTitle>
              <div className="flex gap-4">
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search product..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 bg-gray-50 border-gray-300"
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          
          <Tabs value={activeView} onValueChange={setActiveView}>
            <div className="px-6 pt-4">
              <TabsList className="bg-gray-100">
                <TabsTrigger value="active" className="data-[state=active]:bg-white">
                  <Package className="w-4 h-4 mr-2" /> Active Stock
                </TabsTrigger>
                <TabsTrigger value="low" className="data-[state=active]:bg-white">
                  <AlertTriangle className="w-4 h-4 mr-2" /> Low Stock
                </TabsTrigger>
                <TabsTrigger value="dead" className="data-[state=active]:bg-white">
                  <Clock className="w-4 h-4 mr-2" /> Dead Stock
                </TabsTrigger>
                <TabsTrigger value="damaged" className="data-[state=active]:bg-white">
                  <Trash2 className="w-4 h-4 mr-2" /> Damaged
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Product-wise / Batch-wise switch */}
            <div className="px-6 pt-4 flex flex-col lg:flex-row lg:items-center gap-3">
              <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" data-testid="stock-groupby-switch">
                <button
                  type="button"
                  onClick={() => setGroupBy('product')}
                  className={`px-4 py-2 text-sm transition-colors ${groupBy === 'product' ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  data-testid="stock-groupby-product-btn"
                >
                  Product-wise
                </button>
                <button
                  type="button"
                  onClick={() => setGroupBy('batch')}
                  className={`px-4 py-2 text-sm transition-colors ${groupBy === 'batch' ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  data-testid="stock-groupby-batch-btn"
                >
                  Batch-wise
                </button>
              </div>

              {groupBy === 'batch' && (
                <div className="flex flex-wrap items-center gap-3" data-testid="stock-batch-filters">
                  <Input
                    placeholder="Batch / lot number..."
                    value={batchSearch}
                    onChange={(e) => setBatchSearch(e.target.value)}
                    className="w-56 bg-gray-50 border-gray-300"
                    data-testid="stock-batch-search-input"
                  />
                  <Select value={expiryFilter} onValueChange={setExpiryFilter}>
                    <SelectTrigger className="w-48 bg-gray-50 border-gray-300" data-testid="stock-expiry-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPIRY_FILTERS.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeZero}
                      onChange={(e) => setIncludeZero(e.target.checked)}
                      data-testid="stock-include-zero-toggle"
                    />
                    Show zero-qty batches
                  </label>
                </div>
              )}

              <div className="lg:ml-auto">
                <Button variant="outline" size="sm" onClick={handleExport} data-testid="stock-export-btn">
                  <Download className="w-4 h-4 mr-2" /> Export
                </Button>
              </div>
            </div>

            <CardContent className="pt-4">
              {/* Summary for current view */}
              {stockData && (
                <div className="flex gap-4 mb-4 text-sm">
                  <Badge variant="outline" className="px-3 py-1">
                    Items: {stockData.summary?.total_items}
                  </Badge>
                  <Badge variant="outline" className="px-3 py-1">
                    Total Qty: {stockData.summary?.total_quantity?.toLocaleString()}
                  </Badge>
                  <Badge variant="outline" className="px-3 py-1">
                    Value: ₹{stockData.summary?.total_value?.toLocaleString()}
                  </Badge>
                </div>
              )}

              {stockLoading ? (
                <div className="text-center py-8 text-gray-500">Loading...</div>
              ) : !stockData?.items?.length ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No {activeView} stock found</p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50 border-gray-200">
                        <TableHead className="text-gray-600">Product</TableHead>
                        <TableHead className="text-gray-600">Category</TableHead>
                        <TableHead className="text-gray-600">
                          {groupBy === 'batch' ? 'Batch / Expiry' : 'Batches'}
                        </TableHead>
                        <TableHead className="text-gray-600 text-center">Qty</TableHead>
                        <TableHead className="text-gray-600 text-center">Reorder</TableHead>
                        <TableHead className="text-gray-600 text-right">Cost</TableHead>
                        <TableHead className="text-gray-600 text-right">Selling</TableHead>
                        <TableHead className="text-gray-600 text-right">Value</TableHead>
                        <TableHead className="text-gray-600">Status</TableHead>
                        <TableHead className="text-gray-600 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stockData.items.map((item, index) => (
                        <Fragment key={item.stock_id || `${item.product_id}-${index}`}>
                        <TableRow className="border-gray-200">
                          <TableCell>
                            <div>
                              <p className="text-gray-800 font-medium">{item.product_name}</p>
                              <p className="text-teal-600 text-xs">{item.product_sku}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-600 text-sm">{item.category_name || '-'}</TableCell>
                          <TableCell className="text-gray-600 text-sm">
                            {groupBy === 'batch' ? (
                              <>
                                {item.batch_number || 'Default'}
                                {item.expiry_date && (
                                  <p className="text-xs text-gray-400">
                                    Exp: {formatDateOnly(item.expiry_date)}
                                  </p>
                                )}
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={() => toggleProductBatches(item)}
                                className="flex items-center gap-1 text-teal-700 hover:underline"
                                data-testid={`stock-expand-batches-${index}`}
                              >
                                {expandedProduct === item.product_id ? (
                                  <ChevronDown className="w-3 h-3" />
                                ) : (
                                  <ChevronRight className="w-3 h-3" />
                                )}
                                {item.batch_count || 0} batch{(item.batch_count || 0) === 1 ? '' : 'es'}
                                {item.nearest_expiry && (
                                  <span className="text-xs text-gray-400 ml-1">
                                    (exp {formatDateOnly(item.nearest_expiry)})
                                  </span>
                                )}
                              </button>
                            )}
                          </TableCell>
                          <TableCell className={`text-center font-bold ${
                            item.quantity <= 0 ? 'text-red-600' : 
                            item.is_low_stock ? 'text-amber-600' : 'text-gray-800'
                          }`}>
                            {item.quantity} {item.uom}
                          </TableCell>
                          <TableCell className="text-center text-gray-500">{item.reorder_level}</TableCell>
                          <TableCell className="text-right text-gray-700">₹{item.cost_price?.toFixed(2)}</TableCell>
                          <TableCell className="text-right text-gray-700">₹{item.selling_price?.toFixed(2)}</TableCell>
                          <TableCell className="text-right font-medium text-gray-800">₹{item.stock_value?.toLocaleString()}</TableCell>
                          <TableCell>
                            {item.is_damaged && <Badge className="bg-red-100 text-red-700">Damaged</Badge>}
                            {item.is_low_stock && !item.is_damaged && <Badge className="bg-amber-100 text-amber-700">Low</Badge>}
                            {item.is_dead_stock && !item.is_damaged && <Badge className="bg-gray-100 text-gray-700">Dead</Badge>}
                            {!item.is_damaged && !item.is_low_stock && !item.is_dead_stock && item.quantity > 0 && (
                              <Badge className="bg-emerald-100 text-emerald-700">OK</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleViewMovements(item)} title="View Movements">
                                <Eye className="w-4 h-4 text-gray-500" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleViewBatches(item)} title="View Batches">
                                <Package className="w-4 h-4 text-blue-500" />
                              </Button>
                              {!item.is_damaged && item.quantity > 0 && (
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleMarkDamaged(item)} title="Mark Damaged">
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        {groupBy === 'product' && expandedProduct === item.product_id && (
                          expandedBatches.length === 0 ? (
                            <TableRow className="bg-gray-50" data-testid={`stock-batch-subrow-${index}`}>
                              <TableCell colSpan={10} className="text-sm text-gray-500 pl-8">
                                No batches with stock
                              </TableCell>
                            </TableRow>
                          ) : (
                            expandedBatches.map((b, bi) => (
                              <TableRow key={bi} className="bg-gray-50 border-gray-100" data-testid={bi === 0 ? `stock-batch-subrow-${index}` : undefined}>
                                <TableCell className="pl-8 text-xs text-gray-500">
                                  Batch · {b.warehouse_name || '-'}
                                </TableCell>
                                <TableCell />
                                <TableCell className="text-sm font-medium text-gray-800">
                                  {b.batch_number}
                                  {b.expiry_date && (
                                    <p className="text-xs text-gray-400">Exp: {formatDateOnly(b.expiry_date)}</p>
                                  )}
                                </TableCell>
                                <TableCell className="text-center text-sm font-semibold">{b.quantity}</TableCell>
                                <TableCell />
                                <TableCell className="text-right text-sm">₹{(b.cost_price || 0).toFixed(2)}</TableCell>
                                <TableCell />
                                <TableCell className="text-right text-sm">
                                  ₹{((b.quantity || 0) * (b.cost_price || 0)).toLocaleString()}
                                </TableCell>
                                <TableCell />
                                <TableCell />
                              </TableRow>
                            ))
                          )
                        )}
                      </Fragment>
                    ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Tabs>
        </Card>
      )}

      {/* Stock Movement Dialog */}
      <Dialog open={movementDialogOpen} onOpenChange={setMovementDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800">Stock Movements - {selectedItem?.product_name}</DialogTitle>
          </DialogHeader>
          
          {movements.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No movements found</div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-gray-600">Date</TableHead>
                    <TableHead className="text-gray-600">Type</TableHead>
                    <TableHead className="text-gray-600">From</TableHead>
                    <TableHead className="text-gray-600">To</TableHead>
                    <TableHead className="text-gray-600 text-right">Qty</TableHead>
                    <TableHead className="text-gray-600">Reference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movements.map((mov, i) => (
                    <TableRow key={i} className="border-gray-200">
                      <TableCell className="text-gray-600">{formatDateOnly(mov.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{mov.movement_type}</Badge>
                      </TableCell>
                      <TableCell className="text-gray-600">{mov.from_location_type || '-'}</TableCell>
                      <TableCell className="text-gray-600">{mov.to_location_type}</TableCell>
                      <TableCell className="text-right font-medium">{mov.quantity}</TableCell>
                      <TableCell className="text-teal-600">{mov.reference_number || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setMovementDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batches Dialog */}
      <Dialog open={batchDialogOpen} onOpenChange={setBatchDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-gray-800">Batches - {selectedItem?.product_name}</DialogTitle>
          </DialogHeader>
          
          {batches.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No batches found</div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-gray-600">Batch #</TableHead>
                    <TableHead className="text-gray-600">Warehouse</TableHead>
                    <TableHead className="text-gray-600 text-right">Qty</TableHead>
                    <TableHead className="text-gray-600 text-right">Cost</TableHead>
                    <TableHead className="text-gray-600">Expiry</TableHead>
                    <TableHead className="text-gray-600">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.map((batch, i) => (
                    <TableRow key={i} className="border-gray-200">
                      <TableCell className="font-medium text-gray-800">{batch.batch_number}</TableCell>
                      <TableCell className="text-gray-600">{batch.warehouse_name}</TableCell>
                      <TableCell className="text-right font-bold">{batch.quantity}</TableCell>
                      <TableCell className="text-right text-gray-700">₹{batch.cost_price?.toFixed(2)}</TableCell>
                      <TableCell className="text-gray-600">
                        {batch.expiry_date ? formatDateOnly(batch.expiry_date) : '-'}
                      </TableCell>
                      <TableCell>
                        {batch.is_damaged ? (
                          <Badge className="bg-red-100 text-red-700">Damaged</Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-700">Good</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Damaged Dialog */}
      <AlertDialog open={damageDialogOpen} onOpenChange={setDamageDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Mark Stock as Damaged</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              Product: {selectedItem?.product_name}<br />
              Available Qty: {selectedItem?.quantity}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-gray-700">Damaged Quantity *</Label>
              <Input
                type="number"
                min="1"
                max={selectedItem?.quantity}
                value={damageQty}
                onChange={(e) => setDamageQty(e.target.value)}
                placeholder="Enter quantity"
                className="bg-gray-50 border-gray-300"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700">Reason *</Label>
              <Textarea
                value={damageReason}
                onChange={(e) => setDamageReason(e.target.value)}
                placeholder="Describe the damage reason..."
                className="bg-gray-50 border-gray-300"
              />
            </div>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmMarkDamaged} className="bg-red-500 hover:bg-red-600 text-white">
              Mark as Damaged
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StockOverviewPage;
